import React, { useEffect, useState } from "react";
import "./DigitalOMR.css";
import OmrApi, { omrError } from "./omrApi";
import Notification from "components/Notification";
import Description from "@material-ui/icons/Description";
import LibraryBooks from "@material-ui/icons/LibraryBooks";
import Add from "@material-ui/icons/Add";
import Edit from "@material-ui/icons/Edit";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";

// Unwrap a Laravel-style response: { data: <payload> } or the payload itself.
const unwrap = (resp) => {
  const d = resp && resp.data;
  if (d && Object.prototype.hasOwnProperty.call(d, "data")) return d.data;
  return d;
};
const asList = (payload) =>
  Array.isArray(payload) ? payload : (payload && payload.data) || [];

export default function Chapters() {
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [selected, setSelected] = useState(null); // selected subject object

  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);

  const [newSubject, setNewSubject] = useState("");
  const [chapterDraft, setChapterDraft] = useState(""); // text being typed
  const [pendingChapters, setPendingChapters] = useState([]); // staged tags
  const [savingSubject, setSavingSubject] = useState(false);
  const [savingChapter, setSavingChapter] = useState(false);

  // inline edit: { type: "subject" | "chapter", id, value }
  const [editing, setEditing] = useState(null);
  // delete confirm: { type, row }
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const [notif, setNotif] = useState(null);
  const notify = (type, message) => setNotif({ type, message, id: Date.now() });

  // ---- subjects ----------------------------------------------------------
  const loadSubjects = (keepSelection) => {
    setLoadingSubjects(true);
    OmrApi.listSubjects({ with_chapters: 0 })
      .then((resp) => {
        const list = asList(unwrap(resp));
        setSubjects(list);
        if (!keepSelection && list.length && !selected) setSelected(list[0]);
      })
      .catch((err) => notify("error", omrError(err, "Failed to load subjects.")))
      .finally(() => setLoadingSubjects(false));
  };

  useEffect(() => {
    loadSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- chapters of the selected subject ----------------------------------
  const loadChapters = (subjectId) => {
    if (!subjectId) {
      setChapters([]);
      return;
    }
    setLoadingChapters(true);
    OmrApi.listSubjectChapters(subjectId)
      .then((resp) => setChapters(asList(unwrap(resp))))
      .catch((err) => notify("error", omrError(err, "Failed to load chapters.")))
      .finally(() => setLoadingChapters(false));
  };

  useEffect(() => {
    loadChapters(selected ? selected.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const addSubject = () => {
    const name = newSubject.trim();
    if (!name) return;
    setSavingSubject(true);
    OmrApi.createSubject({ name })
      .then((resp) => {
        const created = unwrap(resp);
        notify("success", "Subject added.");
        setNewSubject("");
        loadSubjects(true);
        if (created && created.id) setSelected(created);
      })
      .catch((err) => notify("error", omrError(err, "Failed to add subject.")))
      .finally(() => setSavingSubject(false));
  };

  // Split on commas, trim, drop blanks/dupes — used for both the live keypress
  // and the final save (so an uncommitted draft isn't lost).
  const mergeTags = (existing, raw) => {
    const seen = new Set(existing.map((x) => x.toLowerCase()));
    const out = [...existing];
    String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => {
        if (!seen.has(s.toLowerCase())) {
          seen.add(s.toLowerCase());
          out.push(s);
        }
      });
    return out;
  };

  const commitDraft = () => {
    setPendingChapters((prev) => mergeTags(prev, chapterDraft));
    setChapterDraft("");
  };

  const removePending = (name) =>
    setPendingChapters((prev) => prev.filter((p) => p !== name));

  // Bulk-create every staged chapter (plus any uncommitted draft) at once.
  const saveChapters = () => {
    if (!selected) return;
    const all = mergeTags(pendingChapters, chapterDraft);
    if (!all.length) return;
    setSavingChapter(true);
    Promise.all(
      all.map((name) =>
        OmrApi.createChapter({ omr_subject_id: selected.id, name }),
      ),
    )
      .then(() => {
        notify("success", `${all.length} chapter${all.length > 1 ? "s" : ""} added.`);
        setPendingChapters([]);
        setChapterDraft("");
        loadChapters(selected.id);
        loadSubjects(true); // refresh chapters_count
      })
      .catch((err) => notify("error", omrError(err, "Failed to add chapters.")))
      .finally(() => setSavingChapter(false));
  };

  const startEdit = (type, row) =>
    setEditing({ type, id: row.id, value: row.name });

  const saveEdit = () => {
    if (!editing) return;
    const name = (editing.value || "").trim();
    if (!name) return;
    const { type, id } = editing;
    setBusy(true);
    const req =
      type === "subject"
        ? OmrApi.updateSubject(id, { name })
        : OmrApi.updateChapter(id, { name });
    req
      .then(() => {
        notify("success", `${type === "subject" ? "Subject" : "Chapter"} updated.`);
        setEditing(null);
        if (type === "subject") {
          loadSubjects(true);
          if (selected && selected.id === id)
            setSelected((p) => ({ ...p, name }));
        } else {
          loadChapters(selected.id);
        }
      })
      .catch((err) => notify("error", omrError(err, "Update failed.")))
      .finally(() => setBusy(false));
  };

  const doDelete = () => {
    if (!del) return;
    const { type, row } = del;
    setBusy(true);
    const req =
      type === "subject"
        ? OmrApi.deleteSubject(row.id)
        : OmrApi.deleteChapter(row.id);
    req
      .then(() => {
        notify("success", `${type === "subject" ? "Subject" : "Chapter"} deleted.`);
        setDel(null);
        if (type === "subject") {
          if (selected && selected.id === row.id) setSelected(null);
          loadSubjects(true);
        } else {
          loadChapters(selected.id);
          loadSubjects(true);
        }
      })
      .catch((err) => notify("error", omrError(err, "Delete failed.")))
      .finally(() => setBusy(false));
  };

  const isEditing = (type, id) =>
    editing && editing.type === type && editing.id === id;

  return (
    <div className="domr-page">
      {notif && (
        <Notification
          key={notif.id}
          type={notif.type}
          title={notif.message}
          duration={3500}
        />
      )}

      <h1 className="domr-page-title">Subjects &amp; Chapters</h1>
      <p className="domr-page-sub">
        Manage the subjects and chapters used across test papers.
      </p>

      <div className="domr-split">
        {/* ---- SUBJECTS ----------------------------------------------------- */}
        <div className="domr-card" style={{ marginBottom: 0 }}>
          <div className="domr-card-head">
            <span className="domr-chip">
              <Description />
            </span>
            <h3>Subjects</h3>
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <input
              className="domr-input"
              placeholder="e.g. Mathematics"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addSubject();
              }}
            />
            <button
              type="button"
              className="domr-btn domr-btn-primary"
              onClick={addSubject}
              disabled={savingSubject || !newSubject.trim()}
              style={{ flexShrink: 0 }}
            >
              <Add style={{ fontSize: 18 }} /> Add
            </button>
          </div>

          <div className="domr-list">
            {loadingSubjects ? (
              <div className="domr-empty">Loading…</div>
            ) : subjects.length === 0 ? (
              <div className="domr-empty">No subjects yet.</div>
            ) : (
              subjects.map((s) => (
                <div
                  key={s.id}
                  className={
                    "domr-list-row" +
                    (selected && selected.id === s.id ? " active" : "")
                  }
                  onClick={() => !isEditing("subject", s.id) && setSelected(s)}
                >
                  {isEditing("subject", s.id) ? (
                    <input
                      className="domr-input"
                      style={{ height: 36 }}
                      autoFocus
                      value={editing.value}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setEditing((p) => ({ ...p, value: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        if (e.key === "Escape") setEditing(null);
                      }}
                    />
                  ) : (
                    <span className="domr-list-name">
                      {s.name}
                      <span className="domr-count">
                        {s.chapters_count != null
                          ? `${s.chapters_count} ch.`
                          : ""}
                      </span>
                    </span>
                  )}

                  <span
                    className="domr-list-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isEditing("subject", s.id) ? (
                      <>
                        <button
                          className="domr-action green"
                          disabled={busy}
                          onClick={saveEdit}
                        >
                          <Check style={{ fontSize: 18 }} />
                        </button>
                        <button
                          className="domr-action"
                          onClick={() => setEditing(null)}
                        >
                          <Close style={{ fontSize: 18 }} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="domr-action blue"
                          onClick={() => startEdit("subject", s)}
                        >
                          <Edit style={{ fontSize: 17 }} />
                        </button>
                        <button
                          className="domr-action red"
                          onClick={() => setDel({ type: "subject", row: s })}
                        >
                          <DeleteOutline style={{ fontSize: 18 }} />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ---- CHAPTERS ----------------------------------------------------- */}
        <div className="domr-card" style={{ marginBottom: 0 }}>
          <div className="domr-card-head">
            <span className="domr-chip">
              <LibraryBooks />
            </span>
            <h3>
              Chapters
              {selected ? (
                <span className="domr-subtle"> — {selected.name}</span>
              ) : null}
            </h3>
          </div>

          {!selected ? (
            <div className="domr-empty">Select a subject to manage its chapters.</div>
          ) : (
            <>
              <div style={{ marginBottom: 14 }}>
                <div
                  className="domr-tag-input"
                  onClick={(e) => {
                    const inp = e.currentTarget.querySelector("input");
                    if (inp) inp.focus();
                  }}
                >
                  {pendingChapters.map((p) => (
                    <span className="domr-tag" key={p}>
                      {p}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePending(p);
                        }}
                      >
                        <Close style={{ fontSize: 13 }} />
                      </button>
                    </span>
                  ))}
                  <input
                    className="domr-tag-field"
                    placeholder={
                      pendingChapters.length
                        ? "Add another…"
                        : "Type a chapter and press Enter"
                    }
                    value={chapterDraft}
                    onChange={(e) => setChapterDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        commitDraft();
                      } else if (
                        e.key === "Backspace" &&
                        !chapterDraft &&
                        pendingChapters.length
                      ) {
                        setPendingChapters((prev) => prev.slice(0, -1));
                      }
                    }}
                    onBlur={commitDraft}
                  />
                </div>
                <div className="domr-hint">
                  Press Enter or comma to add each chapter, then save them all together.
                </div>
              </div>

              <div className="domr-form-actions" style={{ marginBottom: 18 }}>
                <button
                  type="button"
                  className="domr-btn domr-btn-primary"
                  onClick={saveChapters}
                  disabled={
                    savingChapter ||
                    (!pendingChapters.length && !chapterDraft.trim())
                  }
                >
                  <Add style={{ fontSize: 18 }} />
                  {savingChapter
                    ? "Saving…"
                    : pendingChapters.length > 1
                    ? `Add ${pendingChapters.length} Chapters`
                    : "Add Chapter"}
                </button>
              </div>

              <div className="domr-list">
                {loadingChapters ? (
                  <div className="domr-empty">Loading…</div>
                ) : chapters.length === 0 ? (
                  <div className="domr-empty">No chapters in this subject yet.</div>
                ) : (
                  chapters.map((c) => (
                    <div key={c.id} className="domr-list-row">
                      {isEditing("chapter", c.id) ? (
                        <input
                          className="domr-input"
                          style={{ height: 36 }}
                          autoFocus
                          value={editing.value}
                          onChange={(e) =>
                            setEditing((p) => ({ ...p, value: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      ) : (
                        <span className="domr-list-name">{c.name}</span>
                      )}

                      <span className="domr-list-actions">
                        {isEditing("chapter", c.id) ? (
                          <>
                            <button
                              className="domr-action green"
                              disabled={busy}
                              onClick={saveEdit}
                            >
                              <Check style={{ fontSize: 18 }} />
                            </button>
                            <button
                              className="domr-action"
                              onClick={() => setEditing(null)}
                            >
                              <Close style={{ fontSize: 18 }} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="domr-action blue"
                              onClick={() => startEdit("chapter", c)}
                            >
                              <Edit style={{ fontSize: 17 }} />
                            </button>
                            <button
                              className="domr-action red"
                              onClick={() => setDel({ type: "chapter", row: c })}
                            >
                              <DeleteOutline style={{ fontSize: 18 }} />
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      {del && (
        <div
          className="domr-overlay"
          style={{ alignItems: "center" }}
          onClick={() => setDel(null)}
        >
          <div className="domr-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="domr-confirm-head">
              <span className="domr-confirm-ico">
                <DeleteOutline />
              </span>
              <h3>Delete “{del.row.name}”?</h3>
            </div>
            <p>
              {del.type === "subject"
                ? "This will remove the subject and its chapters. This action can’t be undone here."
                : "This will remove the chapter. This action can’t be undone here."}
            </p>
            <div className="domr-form-actions">
              <button
                className="domr-btn domr-btn-ghost"
                onClick={() => setDel(null)}
              >
                Cancel
              </button>
              <button
                className="domr-btn domr-btn-primary"
                style={{
                  background: "linear-gradient(90deg,#ef4444,#dc2626)",
                  boxShadow: "none",
                }}
                disabled={busy}
                onClick={doDelete}
              >
                {busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
